import { useMemo } from 'react';
import { SelectedValues } from '@/components/shared/FilterAndSearchPanel/FilterAndSearchPanel';
import { upperSnakeCase } from '@/utils';
import { usePoiStatusLabels } from './usePoiStatusLabels';

const staticFilters = new Set([
  'isGoverning',
  'paintRegion',
  'isVerified',
  'accessibility',
  'status',
  'isManuallyCreated',
  'unAssignedPoi',
  'mediaAttachment',
  'selectedDeckIds',
]);

export function useMappedFilters(selectedFilterValues: SelectedValues, selectedPOIType?: string) {
  const { getStatusLabelMap } = usePoiStatusLabels();

  const labels = useMemo(() => {
    return getStatusLabelMap(selectedPOIType);
  }, [getStatusLabelMap, selectedPOIType]);

  const selectedPoiFilters = useMemo(() => {
    return Object.entries(selectedFilterValues)
      .filter(([key]) => !staticFilters.has(key))
      .map(([key, value]) => ({ id: key, value: value }));
  }, [selectedFilterValues]);

  const isGoverningFilters = useMemo(() => {
    return selectedFilterValues.isGoverning?.map((item) => item.toLowerCase() === 'yes');
  }, [selectedFilterValues]);

  const isVerifiedFilters = useMemo(() => {
    return selectedFilterValues.isVerified?.map((item) => item.toLowerCase() === 'verified');
  }, [selectedFilterValues]);

  const accessibilityFilters = useMemo(() => {
    return selectedFilterValues.accessibility?.map((item) => upperSnakeCase(item));
  }, [selectedFilterValues]);

  const statusFilters = useMemo(() => {
    if (!selectedFilterValues.status) return [];

    // Create reverse mapping: display label -> key
    const labelToKey: Record<string, string> = {};
    Object.entries(labels).forEach(([key, label]) => {
      labelToKey[label] = key;
    });

    // Map display labels back to keys
    return selectedFilterValues.status.map((displayLabel) => {
      // If the value is already a key (for backward compatibility), return it
      if (labels[displayLabel]) {
        return displayLabel;
      }
      // Otherwise, map the display label to its key
      return labelToKey[displayLabel] || displayLabel;
    });
  }, [selectedFilterValues, labels]);

  const deckFilters = useMemo(() => {
    return selectedFilterValues.selectedDeckIds;
  }, [selectedFilterValues]);

  const isManuallyCreatedFilters = useMemo(() => {
    return selectedFilterValues.isManuallyCreated?.map((item) => item.toLowerCase() === 'yes');
  }, [selectedFilterValues]);

  const unAssignedPoiFilters = useMemo(() => {
    return selectedFilterValues.unAssignedPoi?.map((item) => item.toLowerCase() === 'yes');
  }, [selectedFilterValues]);

  const mediaAttachmentFilters = useMemo(() => {
    return selectedFilterValues.mediaAttachment?.map((item) => item.toLowerCase() === 'yes');
  }, [selectedFilterValues]);

  return {
    selectedPoiFilters,
    isGoverningFilters,
    isVerifiedFilters,
    accessibilityFilters,
    statusFilters,
    deckFilters,
    isManuallyCreatedFilters,
    unAssignedPoiFilters,
    mediaAttachmentFilters,
  };
}
